import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import AssessmentForm from "../components/AssessmentForm";
import { scoreAnswers } from "../lib/scoring";
import { generateInsights } from "../lib/insights";
import { calculateRevenueLift } from "../lib/revenue";
import { storeAssessment } from "../util/http";
import config from "../config/assessmentConfig.json";
import { writeReportState } from "../lib/reportState";
import { trackEvent } from "../lib/analytics";

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const pageTitle = "Restaurant Assessment | Speak Your Menu";
  const pageDescription = "Take the Speak Your Menu restaurant assessment to evaluate menu training, consistency, allergen readiness, and revenue opportunity.";
  const canonicalUrl = "https://speakyourmenu.com/assessment";

  // Always scroll to the top when landing on the assessment page
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, []);

  async function handleSubmit(answers, restaurant, extras = {}) {
    try {
      setError("");
      const scores = scoreAnswers(answers);
      const insights = generateInsights(scores);
      const revenue = calculateRevenueLift(scores);

      const payload = {
        restaurant,
        answers,
        scores,
        insights: insights.map(i => i.message),
        revenue,
        meta: { ua: navigator.userAgent, source: "web-assessment-v1" },
        consent: !!extras.consent,
        marketingOptIn: !!extras.marketingOptIn,
      };

      const { id } = await storeAssessment(payload);

      trackEvent("assessment_complete", {
        assessment_version: "web-assessment-v1",
        page_identifier: "assessment",
        page_path: "/assessment",
      });

      const reportState = {
        id,
        scores,
        insights,
        revenue,
        restaurant,
        createdAt: Date.now(),
      };

      try { sessionStorage.removeItem("assessmentDraft"); } catch {}
      writeReportState(reportState, { source: "assessment-submit" });

      navigate("/assessment/report", { state: reportState });
    } catch (e) {
      setError("We couldn't save your assessment. Please try again.");
    }
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:site" content="@speakyourmenu" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://speakyourmenu.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Assessment",
              item: canonicalUrl,
            },
          ],
        })}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="container py-4">
        <div className="mb-4 assessment-hero">
          <h1 className="mb-1 text-wine">Restaurant Assessment</h1>
          <p className="text-muted mb-0">This questionnaire helps us understand your menu fluency, training, consistency, and upselling habits.</p>
        </div>

        <AssessmentForm config={config} onSubmit={handleSubmit} error={error} />
      </main>
      <Footer />
    </>
  );
}
